import React from 'react'
// import { isBrowser } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import FileUpload from 'react-material-file-upload'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import { useUploadFileWithGrantMutation } from '@/core/services/file'

import { getApiErrorMessage } from '@/utils/errors'
import { getFileMimeType } from '@/utils/files'

const mimeToExtension: Record<string, string> = {
  // Images
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/heic': '.heic', // Common on iOS
  'image/heif': '.heif', // Common on iOS
  'image/webp': '.webp', // Common on Android

  // Documents
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',

  // Videos
  'video/mp4': '.mp4',
  'video/quicktime': '.mov', // Common on iOS
  'video/x-m4v': '.m4v',
  'video/3gpp': '.3gp', // Common on mobile
  'video/webm': '.webm',

  // Audio
  'audio/mpeg': '.mp3',
  'audio/mp4': '.m4a',
  'audio/wav': '.wav',
  'audio/aac': '.aac',

  // Other common types
  'text/plain': '.txt',
  'text/csv': '.csv',
  'application/zip': '.zip',
  'application/x-zip-compressed': '.zip',
}

export function FileUploadPage() {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  // The upload grant is the ONLY credential this page uses. It scopes the upload to
  // a single object server-side; no doctor session/token is read or persisted here.
  const grant = searchParams.get('grant')

  const [uploadFileWithGrant, { isLoading: isUploading }] = useUploadFileWithGrantMutation()

  const handleChangeFile = async (files: File[]) => {
    if (!grant) return
    const file = files[0]

    let finalFile = file
    if (!file.name.includes('.')) {
      const mimeType = file.type || getFileMimeType(file)
      const extension = mimeToExtension[mimeType]

      if (!extension) {
        enqueueSnackbar(t('Unable to determine file type. Please ensure file has an extension.'), { variant: 'error' })
        return
      }

      finalFile = new File([file], `${file.name}${extension}`, {
        type: mimeType,
      })
    }

    const fileWithType = finalFile.type
      ? finalFile
      : new File([finalFile], finalFile.name, {
          type: getFileMimeType(finalFile),
        })

    try {
      await uploadFileWithGrant({ grant, file: fileWithType }).unwrap()
      enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.document.create.error')), { variant: 'error' })
    }
  }

  if (!grant) {
    return <Navigate to="/" />
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <FileUpload
        data-testid="upload-input"
        buttonProps={{
          disabled: isUploading,
          endIcon: isUploading ? <CircularProgress size={20} /> : undefined,
        }}
        buttonText={t('common:button.upload.label')}
        title={t('page.fileUpload.title')}
        maxFiles={1}
        multiple={false}
        onChange={handleChangeFile}
        value={[]} // to not display list of files
        sx={{
          border: 'none',
        }}
      />
    </Box>
  )
}

export default FileUploadPage
